import React, { Component, useMemo, useContext, useCallback } from 'react';
import {
  AbsoluteFill,
  Sequence,
  getRemotionEnvironment,
  useCurrentFrame,
  useVideoConfig,
  useDelayRender,
  useRemotionEnvironment,
} from 'remotion';
import { AlertDialog } from '@crabs-ai/ds-react';
import { ItemSelectedForCropContext } from '../context-provider';
import { FEATURE_CROP_BACKGROUNDS } from '../flags';
import { isPrefetchEnabled, useLocalUrls } from '../caching/load-to-blob-url';
import { deleteItems } from '../state/actions/delete-items';
import {
  useAllItems,
  useAssets,
  useItem,
  useLinkingEnabled,
  useTracks,
  useWriteContext,
} from '../utils/use-context';
import {
  getPlaybackItemDefaultPremountFrames,
  isPlaybackBoundaryMediaPauseDisabled,
} from '../canvas/utils/playback-boundary-premount';
import { resolveGroupGeometry } from './group/group-geometry';
import { InnerLayer } from './inner-layer';
import {
  buildCaptionPresentationRenderData,
  resolveCaptionPresentationGroup,
} from './captions/caption-presentation-merge';
import { usePlaybackBoundaryItemReady } from '../canvas/playback-boundary-readiness';

interface LayerErrorBoundaryProps {
  itemId: string;
  children: React.ReactNode;
  onDeleteItem: () => void;
  onRenderError?: (error: Error) => void;
}

interface LayerErrorBoundaryState {
  hasError: boolean;
  errorMessage: string | null;
  showDeleteDialog: boolean;
}

export class LayerErrorBoundary extends Component<
  LayerErrorBoundaryProps,
  LayerErrorBoundaryState
> {
  constructor(props: LayerErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      errorMessage: null,
      showDeleteDialog: false,
    };
  }

  static getDerivedStateFromError(error: Error): LayerErrorBoundaryState {
    return {
      hasError: true,
      errorMessage: error.message,
      showDeleteDialog: true,
    };
  }

  componentDidCatch(error: Error) {
    this.props.onRenderError?.(error);
  }

  handleConfirmDelete = () => {
    this.props.onDeleteItem();
    this.setState({
      hasError: false,
      errorMessage: null,
      showDeleteDialog: false,
    });
  };

  handleCancel = () => {
    this.setState({ showDeleteDialog: false });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.onRenderError) return null;
      if (getRemotionEnvironment().isRendering) {
        return (
          <AbsoluteFill
            style={{
              backgroundColor: '#1a1a2e',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 60,
            }}
          >
            <div
              style={{
                color: '#ff6b6b',
                fontSize: 42,
                fontFamily: 'system-ui, sans-serif',
                fontWeight: 700,
                textAlign: 'center',
              }}
            >
              Runtime Error
            </div>
            <div
              style={{
                color: '#fff',
                fontSize: 24,
                fontFamily: 'monospace',
                marginTop: 24,
                maxWidth: '80%',
                textAlign: 'center',
                wordBreak: 'break-word',
              }}
            >
              {this.state.errorMessage ?? 'Unknown layer render error'}
            </div>
          </AbsoluteFill>
        );
      }

      return (
        <AlertDialog
          open={this.state.showDeleteDialog}
          setOpen={(open) => this.setState({ showDeleteDialog: open })}
          titleNode="문제가 발생했습니다"
          descriptionNode="문제의 원인이 된 콘텐츠를 삭제해야 진행 가능합니다. 삭제하시겠습니까?"
          cancelText="취소"
          onCancel={this.handleCancel}
          confirmText="삭제"
          onConfirm={this.handleConfirmDelete}
        />
      );
    }

    return this.props.children;
  }
}

const LayerContent: React.FC<{
  itemId: string;
  trackMuted: boolean;
  zIndex?: number;
  audioOnly?: boolean;
  boundaryPremountFor?: number;
  boundaryHoldUntilItemId?: string;
}> = ({
  itemId,
  trackMuted,
  zIndex,
  audioOnly,
  boundaryPremountFor,
  boundaryHoldUntilItemId,
}) => {
  const item = useItem(itemId);
  const { items } = useAllItems();
  const { assets } = useAssets();
  const { tracks } = useTracks();
  const { fps } = useVideoConfig();
  const currentFrame = useCurrentFrame();
  const itemSelectedForCrop = useContext(ItemSelectedForCropContext);
  const itemIsBeingCropped = item?.id === itemSelectedForCrop;
  const boundaryIncomingReady = usePlaybackBoundaryItemReady(
    boundaryHoldUntilItemId,
  );

  const captionPresentationGroup = useMemo(
    () =>
      item?.type === 'captions'
        ? resolveCaptionPresentationGroup({
            itemId: item.id,
            tracks,
            items,
            assets,
            fps,
          })
        : null,
    [assets, fps, item, items, tracks],
  );
  const isCaptionPresentationFollower =
    captionPresentationGroup != null &&
    captionPresentationGroup.items[0]?.id !== item?.id;
  const captionPresentationData = useMemo(
    () =>
      captionPresentationGroup && !isCaptionPresentationFollower
        ? buildCaptionPresentationRenderData({
            items: captionPresentationGroup.items,
            assets,
            fps,
          })
        : null,
    [assets, captionPresentationGroup, fps, isCaptionPresentationFollower],
  );

  const sequenceStyle: React.CSSProperties = useMemo(
    () => ({
      display: 'contents',
      opacity: item?.opacity,
    }),
    [item],
  );

  const styleWhilePremounted: React.CSSProperties = useMemo(
    () => ({
      display: 'block',
    }),
    [],
  );
  const styleWhilePostmounted: React.CSSProperties = useMemo(
    () => ({
      display: 'block',
      // Remotion의 postmount 기본 opacity=0을 덮어 이전 마지막 프레임을 유지한다.
      opacity: item?.opacity ?? 1,
    }),
    [item?.opacity],
  );

  // Player prepares remote scenes before their boundary, with active-media
  // buffering protection unchanged. Export retains its existing policy.
  const environment = getRemotionEnvironment();
  const isPlayerPreview = environment.isPlayer && !environment.isRendering;
  const reactiveLocalUrls = useLocalUrls();
  const premountFor = useMemo(() => {
    if (!item) return boundaryPremountFor ?? 0;
    const assetId =
      item.type === 'video' ? (item as { assetId: string }).assetId : null;
    const defaultPremountFor = getPlaybackItemDefaultPremountFrames({
      item,
      hasLocalUrl: assetId ? Boolean(reactiveLocalUrls[assetId]) : false,
      prefetchEnabled: isPrefetchEnabled(),
      boundaryPremountFrames: boundaryPremountFor ?? 0,
      fps,
      isPlayerPreview,
    });
    return Math.max(defaultPremountFor, boundaryPremountFor ?? 0);
  }, [boundaryPremountFor, fps, item, reactiveLocalUrls, isPlayerPreview]);
  if (!item) return null;

  if (isCaptionPresentationFollower) {
    return null;
  }

  const presentationItem =
    item.type === 'captions' && captionPresentationData
      ? {
          ...item,
          from: captionPresentationData.from,
          durationInFrames: captionPresentationData.durationInFrames,
          captionStartInSeconds: captionPresentationData.captionStartInSeconds,
        }
      : item;
  const geometry =
    presentationItem.type === 'group'
      ? resolveGroupGeometry(presentationItem, items)
      : presentationItem;

  const disableBoundaryMediaPause = isPlaybackBoundaryMediaPauseDisabled({
    currentFrame,
    itemFrom: geometry.from,
    premountFrames: boundaryPremountFor ?? 0,
  });

  return (
    <Sequence
      key={item.id}
      from={geometry.from}
      style={sequenceStyle}
      durationInFrames={Math.abs(geometry.durationInFrames)}
      styleWhilePremounted={styleWhilePremounted}
      styleWhilePostmounted={styleWhilePostmounted}
      premountFor={premountFor}
      postmountFor={
        boundaryHoldUntilItemId && !boundaryIncomingReady ? 3 * fps : 0
      }
    >
      {itemIsBeingCropped && FEATURE_CROP_BACKGROUNDS && !audioOnly ? (
        <InnerLayer
          cropBackground={true}
          item={presentationItem}
          trackMuted={trackMuted}
          zIndex={zIndex}
          disableBoundaryMediaPause={disableBoundaryMediaPause}
        />
      ) : null}
      <InnerLayer
        cropBackground={false}
        item={presentationItem}
        trackMuted={trackMuted}
        zIndex={zIndex}
        audioOnly={audioOnly}
        disableBoundaryMediaPause={disableBoundaryMediaPause}
        captionPresentationData={captionPresentationData ?? undefined}
      />
    </Sequence>
  );
};

const LayerUnmemoized: React.FC<{
  itemId: string;
  trackMuted: boolean;
  zIndex?: number;
  audioOnly?: boolean;
  boundaryPremountFor?: number;
  boundaryHoldUntilItemId?: string;
}> = ({
  itemId,
  trackMuted,
  zIndex,
  audioOnly,
  boundaryPremountFor,
  boundaryHoldUntilItemId,
}) => {
  const { setState } = useWriteContext();
  const environment = useRemotionEnvironment();
  const {cancelRender} = useDelayRender();
  const isLinkingEnabled = useLinkingEnabled();

  const handleDeleteItem = useCallback(() => {
    setState({
      update: (state) =>
        deleteItems(state, [itemId], {
          isLinkingEnabled,
        }),
      commitToUndoStack: true,
    });
  }, [itemId, setState, isLinkingEnabled]);

  return (
    <LayerErrorBoundary
      itemId={itemId}
      onDeleteItem={handleDeleteItem}
      onRenderError={environment.isClientSideRendering ? cancelRender : undefined}
    >
      <LayerContent
        itemId={itemId}
        trackMuted={trackMuted}
        zIndex={zIndex}
        audioOnly={audioOnly}
        boundaryPremountFor={boundaryPremountFor}
        boundaryHoldUntilItemId={boundaryHoldUntilItemId}
      />
    </LayerErrorBoundary>
  );
};

export const Layer = React.memo(LayerUnmemoized);
